import Papa from 'papaparse';
import { PRODUCTS_CSV_URL } from '@config/constants';

interface ProductRow {
  handle: string;
  title: string;
  image: string;
  // Other fields we don't need for now
}

export interface ProductInfo {
  handle: string;
  title: string;
  imageUrl: string;
  productUrl: string;
}

export class ProductsCatalog {
  private static productsMap: Map<string, ProductInfo> = new Map();
  private static isLoaded = false;

  /**
   * Load products catalog from CSV
   */
  static async loadProducts(): Promise<void> {
    if (this.isLoaded) {
      console.log('📦 Products catalog already loaded');
      return;
    }

    try {
      console.log('📥 Fetching products CSV from:', PRODUCTS_CSV_URL);
      
      // Utiliser le cache si disponible
      const CacheService = (await import('./cache-service')).CacheService;
      const response = await CacheService.fetchWithCache(PRODUCTS_CSV_URL);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const csvText = await response.text();

      const result = Papa.parse<ProductRow>(csvText, {
        header: true,
        skipEmptyLines: true,
      });

      // Clear and rebuild the map
      this.productsMap.clear();

      result.data.forEach(row => {
        if (row.handle && row.title) {
          // Parse the image JSON to extract the src
          let imageUrl = '';
          try {
            if (row.image) {
              const imageData = JSON.parse(row.image);
              imageUrl = imageData.src || '';
            }
          } catch (error) {
            console.warn(`Failed to parse image for ${row.handle}:`, error);
          }

          this.productsMap.set(row.handle, {
            handle: row.handle,
            title: row.title,
            imageUrl,
            productUrl: `https://pemlab-paris.fr/products/${row.handle}`,
          });
        }
      });

      this.isLoaded = true;
      console.log('✅ Products catalog loaded:', this.productsMap.size, 'products');
    } catch (error) {
      console.error('❌ Error loading products catalog:', error);
      // Continue without product info in case of error
      this.isLoaded = true;
    }
  }

  /**
   * Get product info by handle
   */
  static getProductInfo(handle: string): ProductInfo | null {
    const info = this.productsMap.get(handle);
    if (!info) {
      console.warn(`Product not found in catalog: ${handle}`);
      // Return a default info
      return {
        handle,
        title: handle.replace(/-/g, ' ').toUpperCase(),
        imageUrl: '',
        productUrl: `https://pemlab-paris.fr/products/${handle}`,
      };
    }
    return info;
  }

  /**
   * Get multiple products info
   */
  static getProductsInfo(handles: string[]): ProductInfo[] {
    return handles
      .map(handle => this.getProductInfo(handle))
      .filter(info => info !== null) as ProductInfo[];
  }

  /**
   * Reset catalog (useful for testing)
   */
  static reset(): void {
    this.productsMap.clear();
    this.isLoaded = false;
  }
}
